import { envConfig } from '#constants/envConfig';

interface NotificationPresentationPlugin {
  claim(options: { notificationId: string }): Promise<{
    claimed: boolean;
    token?: string;
    expiresAt?: number;
  }>;
  finish(options: { notificationId: string; token: string; presented: boolean }): Promise<void>;
}

export interface IosNotificationClaim {
  claimed: boolean;
  expiresAt?: number;
  finish?: (presented: boolean) => void;
}

let plugin: NotificationPresentationPlugin | undefined;

// Web foreground FCM and WS share the same identity without a native bridge.
const WEB_LEASE_MS = 1500;
const WEB_RECEIPT_MS = 600_000;
const WEB_STORAGE_PREFIX = 'teamplay-notification-presented:';
const webClaims = new Map<string, { token: object; expiresAt: number }>();
const claimLocalWebNotification = (id: string): IosNotificationClaim => {
  const now = Date.now();
  for (const [key, entry] of webClaims) if (entry.expiresAt <= now) webClaims.delete(key);
  if (webClaims.has(id)) return { claimed: false };
  if (webClaims.size >= 256) webClaims.delete(webClaims.keys().next().value!);
  const token = {};
  let finished = false;
  webClaims.set(id, { token, expiresAt: now + WEB_LEASE_MS });
  return {
    claimed: true,
    expiresAt: now + WEB_LEASE_MS,
    finish: (presented) => {
      if (finished) return;
      finished = true;
      const entry = webClaims.get(id);
      if (entry?.token !== token) return;
      if (presented && entry.expiresAt > Date.now()) {
        webClaims.set(id, { token, expiresAt: Date.now() + WEB_RECEIPT_MS });
      } else webClaims.delete(id);
    },
  };
};

const webReceiptKey = (id: string) => `${WEB_STORAGE_PREFIX}${encodeURIComponent(id)}`;
const hasWebReceipt = (id: string): boolean => {
  try {
    const key = webReceiptKey(id);
    const saved = window.localStorage.getItem(key);
    const expiry = Number(saved);
    if (saved !== null && Number.isFinite(expiry) && expiry > Date.now()) return true;
    if (saved !== null) window.localStorage.removeItem(key);
  } catch {
    // Storage-disabled browsers still retain the per-window delivery path.
  }
  return false;
};

const rememberWebPresentation = (id: string) => {
  try {
    const storage = window.localStorage;
    const ownKey = webReceiptKey(id);
    // Per-event keys avoid read/modify/write races between separate browser processes.
    storage.setItem(ownKey, String(Date.now() + WEB_RECEIPT_MS));
    const live: Array<{ key: string; expiry: number }> = [];
    const expired: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (!key?.startsWith(WEB_STORAGE_PREFIX)) continue;
      const expiry = Number(storage.getItem(key));
      if (Number.isFinite(expiry) && expiry > Date.now()) live.push({ key, expiry });
      else expired.push(key);
    }
    for (const key of expired) storage.removeItem(key);
    const oldest = live
      .filter((entry) => entry.key !== ownKey)
      .toSorted((left, right) => left.expiry - right.expiry);
    for (const entry of oldest.slice(0, Math.max(0, live.length - 256)))
      storage.removeItem(entry.key);
  } catch {
    // The receipt cache is optional.
  }
};

const deferred = <T>() => {
  let complete!: (value: T) => void;
  let settled = false;
  const promise = new Promise<T>((resolve) => {
    complete = resolve;
  });
  return {
    promise,
    get settled() {
      return settled;
    },
    settle: (value: T) => {
      if (settled) return;
      settled = true;
      complete(value);
    },
  };
};

const holdWebClaim = (id: string, claim: IosNotificationClaim) => {
  const released = deferred<void>();
  let finished = false;
  const finish = (presented: boolean) => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    const timelyPresentation = presented && (claim.expiresAt ?? 0) > Date.now();
    claim.finish?.(timelyPresentation);
    if (timelyPresentation) rememberWebPresentation(id);
    released.settle();
  };
  const timer = setTimeout(() => finish(false), WEB_LEASE_MS);
  return { claim: { ...claim, finish }, released: released.promise };
};

/** Serialize each event across windows; a frozen owner cannot block unrelated alerts or waiters. */
const claimWebNotification = async (id: string): Promise<IosNotificationClaim> => {
  if ((webClaims.get(id)?.expiresAt ?? 0) > Date.now()) return { claimed: false };
  if (typeof navigator === 'undefined' || !navigator.locks) return claimLocalWebNotification(id);
  const result = deferred<IosNotificationClaim>();
  const controller = new AbortController();
  const fallback = () => {
    if (result.settled) return;
    if (hasWebReceipt(id)) {
      result.settle({ claimed: false });
      return;
    }
    const claim = claimLocalWebNotification(id);
    result.settle(claim.claimed ? holdWebClaim(id, claim).claim : claim);
  };
  const waitTimer = setTimeout(() => {
    controller.abort();
    // Resolve independently of the LockManager's rejection, even if its queue stalls.
    fallback();
  }, WEB_LEASE_MS);
  try {
    void navigator.locks
      .request(
        `teamplay-notification-presentation:${id}`,
        { signal: controller.signal },
        async () => {
          clearTimeout(waitTimer);
          if (controller.signal.aborted || result.settled) return;
          if (hasWebReceipt(id)) {
            result.settle({ claimed: false });
            return;
          }
          const claim = claimLocalWebNotification(id);
          if (!claim.claimed) {
            result.settle(claim);
            return;
          }
          const held = holdWebClaim(id, claim);
          result.settle(held.claim);
          await held.released;
        }
      )
      .catch(() => {
        clearTimeout(waitTimer);
        fallback();
      });
  } catch {
    clearTimeout(waitTimer);
    fallback();
  }
  return result.promise;
};

/** Shared native reservation for every alert event, including foreground push fallback. */
export async function claimIosNotification(
  _type: string,
  notificationId?: string | null
): Promise<IosNotificationClaim> {
  if (!notificationId) return { claimed: true };
  if (!envConfig.IS_NATIVE) return claimWebNotification(notificationId);
  try {
    const { Capacitor, registerPlugin } = await import('@capacitor/core');
    if (!['ios', 'android'].includes(Capacitor.getPlatform())) return { claimed: true };
    plugin ??= registerPlugin<NotificationPresentationPlugin>('NotificationPresentation');
    const result = await plugin.claim({ notificationId });
    const token = result.token;
    return {
      claimed: result.claimed,
      expiresAt: result.expiresAt,
      ...(token
        ? {
            finish: (presented: boolean) => {
              void plugin?.finish({ notificationId, token, presented }).catch(() => {});
            },
          }
        : {}),
    };
  } catch {
    return { claimed: true };
  }
}
